import { Space, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { Tenant } from '../types/tenant.types'
import { formatFeatureFlag, formatCountryCodes, sortFeatureFlags } from '../types/tenant.types'
import { FeatureFlagTag } from './FeatureFlagTag'
import './TenantTable.css'

const { Text } = Typography

interface TenantTableProps {
  tenants: Tenant[]
  loading: boolean
  showInactive: boolean
  onRowClick?: (tenantCode: string) => void
  selectedTenantCode?: string | null
}

export function TenantTable({ tenants, loading, showInactive, onRowClick, selectedTenantCode }: TenantTableProps) {
  const hasAnyInactiveDate = tenants.some(t => !!t.inactiveDate)

  const columns: ColumnsType<Tenant> = [
    {
      title: 'Workspace',
      dataIndex: 'tenantName',
      key: 'tenantName',
      width: 300,
      sorter: (a, b) => a.tenantName.localeCompare(b.tenantName),
      render: (tenantName: string, record: Tenant) => (
        <Space size={8} wrap={false} style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}>
          <Text ellipsis style={{ maxWidth: '150px' }}>
            {tenantName}
          </Text>
          <span>
            <Tag color="geekblue">{record.tenantCode.toLowerCase()}</Tag>
            <Tag color="default">{record.workspaceCode.toLowerCase()}</Tag>
          </span>
        </Space>
      )
    },
    {
      title: 'Countries',
      dataIndex: 'countryCodes',
      key: 'countryCodes',
      width: 130,
      sorter: (a, b) => (a.countryCodes || '').localeCompare(b.countryCodes || ''),
      render: (countryCodes: string) => formatCountryCodes(countryCodes)
    },
    {
      title: 'Time zone',
      dataIndex: 'timeZoneId',
      key: 'timeZoneId',
      width: 150,
      ellipsis: true,
      sorter: (a, b) => a.timeZoneId.localeCompare(b.timeZoneId)
    },
    {
      title: 'Type',
      dataIndex: 'workspaceType',
      key: 'workspaceType',
      width: hasAnyInactiveDate ? 180 : 100,
      sorter: (a, b) => (a.workspaceType || '').localeCompare(b.workspaceType || ''),
      render: (type: string | null, record: Tenant) => {
        const shortLabels: Record<string, string> = { new: 'New', demo: 'Demo', trial: 'Trial', uat: 'UAT' }
        const colors: Record<string, string> = { new: 'blue', demo: 'orange', trial: 'purple', uat: 'magenta' }
        const label = (type && shortLabels[type]) || type || '-'
        const color = (type && colors[type]) || 'default'
        const inactiveDate = record.inactiveDate ? new Date(record.inactiveDate) : null
        return (
          <Space size={6} wrap={false} style={{ whiteSpace: 'nowrap' }}>
            <Tag color={color} style={{ marginRight: 0 }}>{label}</Tag>
            {inactiveDate && (
              <Tooltip title={`Inactive date: ${inactiveDate.toLocaleDateString()}`}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  exp {inactiveDate.toLocaleDateString()}
                </Text>
              </Tooltip>
            )}
          </Space>
        )
      }
    },
    ...(showInactive
      ? [
          {
            title: 'Status',
            dataIndex: 'inactiveDate',
            key: 'status',
            width: 90,
            align: 'center' as const,
            sorter: (a: Tenant, b: Tenant) => {
              // Order: Active (null) → Scheduled (future) → Inactive (past/now)
              const stateOf = (t: Tenant) => {
                if (!t.inactiveDate) return 0
                return new Date(t.inactiveDate).getTime() > Date.now() ? 1 : 2
              }
              return stateOf(a) - stateOf(b)
            },
            render: (inactiveDate: string | null) => {
              if (!inactiveDate) {
                return (
                  <Tag color="green" style={{ fontSize: '11px', padding: '0 7px' }}>
                    Active
                  </Tag>
                )
              }
              const date = new Date(inactiveDate)
              const formatted = date.toLocaleDateString()
              const isFuture = date.getTime() > Date.now()
              return isFuture ? (
                <Tooltip title={`Deactivates on ${formatted}`}>
                  <Tag color="gold" style={{ fontSize: '11px', padding: '0 7px' }}>
                    Scheduled
                  </Tag>
                </Tooltip>
              ) : (
                <Tooltip title={`Inactive since ${formatted}`}>
                  <Tag color="red" style={{ fontSize: '11px', padding: '0 7px' }}>
                    Inactive
                  </Tag>
                </Tooltip>
              )
            }
          }
        ]
      : []),
    {
      title: 'Feature flags',
      dataIndex: 'featureFlags',
      key: 'featureFlags',
      width: 250,
      sorter: (a, b) => {
        const aCount = a.featureFlags.filter(ff => ff.disabledDate === null).length
        const bCount = b.featureFlags.filter(ff => ff.disabledDate === null).length
        return aCount - bCount
      },
      render: (featureFlags: Tenant['featureFlags']) => {
        const enabledFlags = sortFeatureFlags(featureFlags.filter(ff => ff.disabledDate === null))
        const displayFlags = enabledFlags.slice(0, 3)
        const remaining = enabledFlags.length - displayFlags.length
        const remainingFlags = enabledFlags.slice(3)

        return (
          <div style={{ display: 'flex', gap: '4px' }}>
            {displayFlags.map(ff => (
              <FeatureFlagTag key={ff.featureFlag} flag={ff.featureFlag} />
            ))}
            {remaining > 0 && (
              <Tooltip
                title={
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {remainingFlags.map(ff => (
                      <div key={ff.featureFlag}>{formatFeatureFlag(ff.featureFlag)}</div>
                    ))}
                  </div>
                }
              >
                <Tag style={{ fontSize: '11px', padding: '0 7px', cursor: 'pointer' }}>+{remaining} more</Tag>
              </Tooltip>
            )}
          </div>
        )
      }
    },
    {
      title: 'Users',
      dataIndex: 'userCount',
      key: 'userCount',
      width: 60,
      align: 'center',
      sorter: (a, b) => (a.userCount || 0) - (b.userCount || 0),
      render: (userCount?: number) => userCount ?? '-'
    },
    {
      title: 'Created',
      dataIndex: 'createdDate',
      key: 'createdDate',
      width: 100,
      sorter: (a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime(),
      render: (createdDate: string) => new Date(createdDate).toLocaleDateString()
    }
  ]

  return (
    <div className="tenant-table-wrapper" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Table
        size="small"
        columns={columns}
        dataSource={tenants}
        loading={loading}
        rowKey="tenantCode"
        scroll={{ x: 'max-content', y: 'calc(100vh - 200px)' }}
        onRow={record => ({
          onClick: () => onRowClick?.(record.tenantCode),
          className: selectedTenantCode === record.tenantCode ? 'tenant-row-selected' : '',
          style: {
            cursor: 'pointer'
          }
        })}
        pagination={false}
        style={{ flex: 1 }}
      />
    </div>
  )
}
